import * as ScrollArea from '@radix-ui/react-scroll-area';
import { cx } from 'linaria';
import * as React from 'react';
import { default as IconMenuCollapse } from '../../../icons/16px/menu-collapse.svg';
import { ThemeProvider } from '../theme-provider';
import { Tooltip } from '../tooltip';
import { SideMenuContext, SideMenuContextShape, SideMenuItemRenderer } from './side-menu.context';
import * as styles from './side-menu.styles';

export interface SideMenuProps {
    /**
     * Items that stick to the bottom and do not scroll
     */
    affixBottom?: React.ReactElement;

    /**
     * Whether the menu should be expanded by default.
     */
    defaultExpanded?: boolean;

    /**
     * Callback when the menu is expanded or collased by clicking the expand/collapse button.
     */
    onExpandCollapse?: (expanded: boolean) => void;

    /**
     * Values of items that should be expanded initially.
     */
    defaultExpandedItems?: string[];

    /**
     * The default value of the selected item (uncontrolled). Setting this will expand this item and all of its ancestors.
     */
    defaultSelected?: string;

    /**
     * Controlled value of selected item.
     */
    selected?: string;

    /**
     * Disable expand/collapse button.
     */
    disabledExpandCollapse?: boolean;

    /**
     * Use the item value as an href.
     */
    itemRenderer?: SideMenuItemRenderer;

    /**
     *  Test id for unit and e2e
     */
    testId?: string;
}

type SideMenuComponent = React.ForwardRefExoticComponent<
    React.PropsWithChildren<SideMenuProps> & React.RefAttributes<HTMLDivElement>
>;

const getParentsOfSelected = (myChildren: React.ReactNode, mySelected: string): string[] => {
    let myExpanded: string[] = [];
    const items = React.Children.toArray(myChildren);

    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!React.isValidElement(item)) {
            return;
        }

        const { props } = item;

        if (props.value !== undefined && props.value === mySelected) {
            return [props.value];
        }

        if (props.children) {
            const childExpanded = getParentsOfSelected(props.children, mySelected);
            if (childExpanded.length) {
                childExpanded.push(props.value);
                myExpanded = [...childExpanded];
            }
        }
    }

    return myExpanded.filter((myValue) => myValue !== undefined);
};

export const SideMenu: SideMenuComponent = React.forwardRef(function SideMenuWithForwardRef(
    props: React.PropsWithChildren<SideMenuProps>,
    ref,
) {
    const {
        testId,
        children,
        affixBottom,
        defaultExpanded = false,
        defaultExpandedItems = [],
        onExpandCollapse,
        defaultSelected,
        selected,
        disabledExpandCollapse = false,
        itemRenderer,
    } = props;
    const [expandedItems, setExpandedItems] = React.useState<string[]>(defaultExpandedItems);
    const [selectedActual, setSelectedActual] = React.useState<string>(defaultSelected);
    const [expanded, setExpanded] = React.useState<boolean>(defaultExpanded);
    const [hovered, setHovered] = React.useState<boolean>(false);

    const context: SideMenuContextShape = {
        props,
        selectedActual,
        setSelectedActual,
        expandedItems,
        setExpandedItems,
        itemRenderer,
    };

    const handleOpen: React.MouseEventHandler<HTMLButtonElement> = () => {
        const newExpanded = !expanded;
        setExpanded(newExpanded);
        if (!newExpanded) {
            setHovered(false);
        }
        onExpandCollapse && onExpandCollapse(newExpanded);
    };

    const handleRootMouseOver: React.MouseEventHandler<HTMLDivElement> = () => {
        if (!disabledExpandCollapse) {
            setHovered(true);
            onExpandCollapse && onExpandCollapse(true);
        }
    };

    const handleRootMouseLeave: React.MouseEventHandler<HTMLDivElement> = () => {
        if (!disabledExpandCollapse) {
            setHovered(false);
            onExpandCollapse && onExpandCollapse(false);
        }
    };

    const handleButtonMouseOver: React.MouseEventHandler<HTMLButtonElement> = (event) => {
        event.stopPropagation();
        event.preventDefault();
    };

    // Set selectedActual when selected is changed
    React.useEffect(() => {
        if (selected !== undefined) {
            setSelectedActual(selected);
        }
    }, [children, selected]);

    // Ensure all parents of selected item are expanded
    React.useEffect(() => {
        if (selectedActual) {
            setExpandedItems((expandedItems) =>
                [...getParentsOfSelected(children, selectedActual), ...expandedItems].filter(
                    (value, index, self) => self.indexOf(value) === index,
                ),
            );
        }
    }, [children, selectedActual]);

    const collapseLabel = expanded ? 'Collapse' : 'Expand';

    return (
        <div ref={ref} data-testid={testId} className={cx(styles.root)} data-open={expanded ? 'yes' : 'no'}>
            <div className={cx(styles.hiddenRoot)}>
                <div
                    className={cx(styles.visibleRoot, hovered && 'hover')}
                    onMouseOver={handleRootMouseOver}
                    onMouseLeave={handleRootMouseLeave}
                >
                    <ThemeProvider theme="dark">
                        <div className={cx(disabledExpandCollapse ? styles.contentDisabled : styles.content)}>
                            <Tooltip text={collapseLabel} position="right" noArrow>
                                <button
                                    aria-label={collapseLabel}
                                    className={cx(styles.collapseButton)}
                                    onClick={disabledExpandCollapse ? undefined : handleOpen}
                                    onMouseOver={handleButtonMouseOver}
                                >
                                    <span
                                        className={cx(
                                            disabledExpandCollapse
                                                ? styles.collapseButtonDisabled
                                                : styles.collapseButtonInside,
                                        )}
                                    >
                                        <IconMenuCollapse />
                                    </span>
                                </button>
                            </Tooltip>
                            <ScrollArea.Root className={cx(styles.scrollArea)} type="scroll">
                                <ScrollArea.Viewport className={cx(styles.scrollViewport)}>
                                    <SideMenuContext.Provider value={context}>
                                        <div className={cx(styles.children)}>{children}</div>
                                    </SideMenuContext.Provider>
                                </ScrollArea.Viewport>
                                <ScrollArea.Scrollbar className={cx(styles.scrollbar)}>
                                    <ScrollArea.Thumb className={cx(styles.scrollbarThumb)} />
                                </ScrollArea.Scrollbar>
                            </ScrollArea.Root>
                            {affixBottom ? <div className={cx(styles.affixBottom)}>{affixBottom}</div> : null}
                        </div>
                    </ThemeProvider>
                </div>
            </div>
        </div>
    );
});

SideMenu.displayName = 'SideMenu';

export default SideMenu;
